// Bundle builder on the product page ("Finish the look").
// Adds every row with a quantity > 0 to the cart in one request, then lets the
// theme refresh its cart via the `apps:product-added-to-cart` hook in theme.js.
if (!customElements.get("product-bundle")) {
  customElements.define(
    "product-bundle",
    class ProductBundle extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll("[data-bundle-item]"));
        this.totalEl = this.querySelector("[data-bundle-total]");
        this.submitBtn = this.querySelector("[data-bundle-submit]");
        this.errorEl = this.querySelector("[data-bundle-error]");

        this.items.forEach((item) => this.bindItem(item));

        const clear = this.querySelector("[data-bundle-clear]");
        if (clear) clear.addEventListener("click", () => this.clearAll());
        if (this.submitBtn) this.submitBtn.addEventListener("click", () => this.submit());

        // Keep the current product's row in sync with the main variant picker.
        this.onVariantChange = (evt) => {
          const variant = evt.detail && evt.detail.variant;
          const current = this.querySelector("[data-bundle-current]");
          if (!variant || !current) return;
          const select = current.querySelector("[data-bundle-variant]");
          if (!select.querySelector(`option[value="${variant.id}"]`)) return;
          select.value = String(variant.id);
          this.updateItem(current);
        };
        document.addEventListener("flu:product:variant-change", this.onVariantChange);

        this.updateTotal();
      }

      disconnectedCallback() {
        document.removeEventListener("flu:product:variant-change", this.onVariantChange);
      }

      bindItem(item) {
        const input = item.querySelector("[data-bundle-qty]");
        const select = item.querySelector("[data-bundle-variant]");
        const toggle = item.querySelector("[data-bundle-options-toggle]");

        if (input) {
          item.querySelector("[data-bundle-minus]").addEventListener("click", () => this.setQty(input, this.getQty(input) - 1));
          item.querySelector("[data-bundle-plus]").addEventListener("click", () => this.setQty(input, this.getQty(input) + 1));
          input.addEventListener("change", () => this.setQty(input, this.getQty(input)));
        }

        if (select) select.addEventListener("change", () => this.updateItem(item));

        if (toggle && select) {
          toggle.addEventListener("click", () => {
            const open = select.hidden;
            select.hidden = !open;
            toggle.setAttribute("aria-expanded", String(open));
            toggle.textContent = open ? "Hide options" : "Show options";
          });
        }
      }

      getQty(input) {
        const value = parseInt(input.value, 10);
        return Number.isNaN(value) ? 0 : value;
      }

      setQty(input, value) {
        input.value = Math.max(0, value);
        this.updateTotal();
      }

      updateItem(item) {
        const option = item.querySelector("[data-bundle-variant]").selectedOptions[0];
        if (!option) return;
        const title = item.querySelector("[data-bundle-variant-title]");
        const image = item.querySelector("[data-bundle-image]");
        item.querySelector("[data-bundle-price]").textContent = option.dataset.priceFormatted;
        if (title) title.textContent = option.dataset.title;
        if (image && option.dataset.image) {
          image.removeAttribute("srcset");
          image.src = option.dataset.image;
        }
        this.updateTotal();
      }

      selectedLines() {
        return this.items
          .map((item) => {
            const input = item.querySelector("[data-bundle-qty]");
            const option = item.querySelector("[data-bundle-variant]").selectedOptions[0];
            if (!input || !option || option.disabled) return null;
            const quantity = this.getQty(input);
            if (quantity < 1) return null;
            return { id: Number(option.value), quantity, price: Number(option.dataset.price) };
          })
          .filter(Boolean);
      }

      updateTotal() {
        const lines = this.selectedLines();
        const total = lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
        if (this.totalEl) this.totalEl.textContent = formatMoney(total);
        if (this.submitBtn) this.submitBtn.disabled = lines.length === 0;
      }

      clearAll() {
        this.items.forEach((item) => {
          const input = item.querySelector("[data-bundle-qty]");
          if (input) input.value = 0;
        });
        this.updateTotal();
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || "";
        this.errorEl.hidden = !message;
      }

      async submit() {
        const lines = this.selectedLines();
        if (!lines.length) return;

        this.showError("");
        this.submitBtn.disabled = true;
        this.submitBtn.classList.add("loading");

        try {
          const res = await fetch(`${window.theme.routes.cart.add}.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: lines.map(({ id, quantity }) => ({ id, quantity })) }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message);

          if (this.dataset.cartType !== "flyout") {
            window.location.href = window.theme.routes.cart.base;
            return;
          }

          // theme.js refetches the cart on this event and updates the drawer + header count.
          document.dispatchEvent(new CustomEvent("apps:product-added-to-cart"));
          const toggle = document.querySelector('.js-cart-drawer-toggle[aria-expanded="false"]');
          if (toggle) setTimeout(() => toggle.click(), 300);
        } catch (err) {
          this.showError(err.message || this.dataset.errorMessage);
        } finally {
          this.submitBtn.classList.remove("loading");
          this.updateTotal();
        }
      }
    }
  );
}

function formatMoney(cents) {
  const format = (window.theme && window.theme.moneyFormat) || "${{amount}}";
  const withDelimiters = (number, precision, thousands = ",", decimal = ".") => {
    const [whole, fraction] = (number / 100).toFixed(precision).split(".");
    const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? grouped + decimal + fraction : grouped;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case "amount_no_decimals":
        return withDelimiters(cents, 0);
      case "amount_with_comma_separator":
        return withDelimiters(cents, 2, ".", ",");
      case "amount_no_decimals_with_comma_separator":
        return withDelimiters(cents, 0, ".", ",");
      case "amount_with_apostrophe_separator":
        return withDelimiters(cents, 2, "'", ".");
      default:
        return withDelimiters(cents, 2);
    }
  });
}
